import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import ExecutionTable from '../components/common/ExecutionTable';
import { useExecution } from '../contexts/ExecutionContext';
import api from '../services/api';

const MessageSummarizationPage = () => {
  // Get execution context
  const {
    isModuleRunning,
    getModuleProgress,
    getRunningExecutionId,
    startExecution,
    isAnyModuleRunning,
    getRunningModuleName,
    executionCompletionTrigger,
    executionStartTrigger
  } = useExecution();
  
  // States for execution data
  const [executions, setExecutions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Pagination state for executions
  const [executionPage, setExecutionPage] = useState(1);
  const [executionTotalPages, setExecutionTotalPages] = useState(1);
  const [executionSortOrder, setExecutionSortOrder] = useState("desc");
  
  // State for error messages
  const [error, setError] = useState(null);

  // Module-specific state derived from centralized execution context
  const moduleName = "message_summarization";
  const isRunning = isModuleRunning(moduleName);
  const runProgress = getModuleProgress(moduleName);
  const currentExecutionId = getRunningExecutionId(moduleName);

  // Fetch executions
  const fetchExecutions = useCallback(async () => {
    try {
      const response = await api.getExecutions(
        "message_summarization",
        "timestamp_start",
        executionSortOrder,
        executionPage,
        10
      );
      setExecutions(response.data?.executions || []);
      setExecutionTotalPages(response.data?.nr_of_pages || 1);
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError('Failed to load execution history.');
    }
  }, [executionPage, executionSortOrder]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await api.getBasicStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics.');
    }
  }, []);

  // Run message summarizer
  const runMessageSummarizer = async () => {
    try {
      // Check if any module is already running
      if (isAnyModuleRunning()) {
        const runningModule = getRunningModuleName();
        setError(`Cannot start message summarization. ${runningModule} is currently running.`);
        return;
      }

      const response = await api.runMessageSummarizer(true); // Pass run_in_background=true
      
      if (response.data && response.data.execution_id) {
        if (response.data.error_flag) {
          throw new Error(`Failed to start message summarization process: ${response.data.error_message || 'Unknown error'}`);
        }
        // Start tracking this execution in the centralized state
        startExecution(moduleName, response.data.execution_id);
      } else {
        throw new Error('No execution ID returned');
      }
    } catch (err) {
      console.error('Error running message summarizer:', err);
      setError('Failed to start message summarization process.');
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchExecutions();
    fetchStatistics();
  }, [fetchExecutions, fetchStatistics]);

  // Refresh data when any execution completes
  useEffect(() => {
    if (executionCompletionTrigger > 0) {
      // An execution has completed, refresh data
      fetchExecutions();
      fetchStatistics();
    }
  }, [executionCompletionTrigger, fetchExecutions, fetchStatistics]);

  // Refresh execution history when any execution starts
  useEffect(() => {
    if (executionStartTrigger > 0) {
      // An execution has started, refresh execution history to show the new running entry
      fetchExecutions();
    }
  }, [executionStartTrigger, fetchExecutions]);

  return (
    <Container className="py-4">
      <h1 className="mb-4">Message Summarization</h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>Message Summarization Statistics</Card.Header>
            <Card.Body>
              {statistics ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <span><b>Messages Ready To Process:</b></span>
                    <span className="fw-bold">{statistics.nr_messages_without_summary_ready || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Messages Summarized:</span>
                    <span className="fw-bold">{statistics.nr_messages_with_summary || 0}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>Run Message Summarization</Card.Header>
            <Card.Body>
              <Button 
                variant="primary" 
                onClick={runMessageSummarizer}
                disabled={isRunning}
                className="w-100"
              >
                {isRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Running... ({runProgress}%)
                  </>
                ) : (
                  'Run Message Summarization'
                )}
              </Button>
              
              {isRunning && (
                <div className="mt-3">
                  <div className="progress">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${runProgress}%` }}
                      aria-valuenow={runProgress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {runProgress}%
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col>
          <h3>Execution History</h3>
          <ExecutionTable
            executions={executions}
            title=""
            currentPage={executionPage}
            totalPages={executionTotalPages}
            pageSize={10}
            sortOrder={executionSortOrder}
            onPageChange={(page) => setExecutionPage(page)}
            onSortChange={(sortOrder) => setExecutionSortOrder(sortOrder)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default MessageSummarizationPage;